export interface BalanceInfo {
  index: number // uint16 (2 bytes)
  supplyShares: bigint // uint256 (32 bytes)
  borrowShares: bigint // uint128 (16 bytes)
  supplyAssets: bigint // uint256 (32 bytes)
  borrowAssets: bigint // uint256 (32 bytes)
  collateral: bigint // uint128 (16 bytes)
}

const WORD = 32
const HALF = 16

/** Read `len` bytes at `off` as a big‐endian BigInt. */
function readBigIntBE(buf: Uint8Array, off: number, len: number): bigint {
  let v = 0n
  for (let i = 0; i < len; i++) {
    v = (v << 8n) | BigInt(buf[off + i])
  }
  return v
}

/**
 * Decode:
 *  [ uint16 count (2 bytes) ]
 *  then for each of `count` records:
 *    uint16  index (2 bytes),
 *    uint256 supplyShares (32 bytes),
 *    uint128 borrowShares (16 bytes),
 *    uint256 supplyAssets (32 bytes),
 *    uint256 borrowAssets (32 bytes),
 *    uint128 collateral   (16 bytes)
 */
export function decodePackedMorphoUserDataset(hex: string): BalanceInfo[] {
  if (hex.startsWith('0x')) hex = hex.slice(2)
  const buf = Uint8Array.from(hex.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)))

  // 1) count (uint16)
  const count = Number(readBigIntBE(buf, 0, 2))
  const out: BalanceInfo[] = []

  let off = 2
  for (let i = 0; i < count; i++) {
    // 2) index (uint16)
    const index = Number(readBigIntBE(buf, off, 2))
    off += 2

    // 3) supplyShares (uint256)
    const supplyShares = readBigIntBE(buf, off, WORD)
    off += WORD

    // 4) borrowShares (uint128)
    const borrowShares = readBigIntBE(buf, off, HALF)
    off += HALF

    // 5) supplyAssets (uint256)
    const supplyAssets = readBigIntBE(buf, off, WORD)
    off += WORD

    // 6) borrowAssets (uint256)
    const borrowAssets = readBigIntBE(buf, off, WORD)
    off += WORD

    // 7) collateral (uint128)
    const collateral = readBigIntBE(buf, off, HALF)
    off += HALF

    out.push({
      index,
      supplyShares,
      borrowShares,
      supplyAssets,
      borrowAssets,
      collateral,
    })
  }

  return out
}

export interface ListaBalanceInfo {
  index: number // uint16 (index into marketsIds)
  supplyShares: bigint // uint256
  borrowShares: bigint // uint128
  supplyAssets: bigint // uint256
  borrowAssets: bigint // uint256
  collateral: bigint // uint128
}

export interface ListaUserData {
  whitelistFlags: boolean[] // length = marketsIds.length
  balances: ListaBalanceInfo[]
}

/**
 * Decode Lista user data with leading whitelist flags.
 *
 * @param hex          ABI-encoded hex string
 * @param marketsCount marketsIds.length (needed to slice flags)
 */
export function decodePackedListaUserDataset(
  hex: string,
  marketsCount: number,
): ListaUserData {
  if (hex.startsWith('0x')) hex = hex.slice(2)
  const buf = Uint8Array.from(hex.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)))

  let off = 0

  // 1) whitelist flags
  const whitelistFlags: boolean[] = new Array(marketsCount)
  for (let i = 0; i < marketsCount; i++) {
    whitelistFlags[i] = buf[off++] === 1
  }

  // 2) totalCount (uint16)
  const totalCount = Number(readBigIntBE(buf, off, 2))
  off += 2

  const balances: ListaBalanceInfo[] = []

  // 3) balance records
  for (let i = 0; i < totalCount; i++) {
    const index = Number(readBigIntBE(buf, off, 2))
    off += 2

    const supplyShares = readBigIntBE(buf, off, WORD)
    off += WORD

    const borrowShares = readBigIntBE(buf, off, HALF)
    off += HALF

    const supplyAssets = readBigIntBE(buf, off, WORD)
    off += WORD

    const borrowAssets = readBigIntBE(buf, off, WORD)
    off += WORD

    const collateral = readBigIntBE(buf, off, HALF)
    off += HALF

    balances.push({
      index,
      supplyShares,
      borrowShares,
      supplyAssets,
      borrowAssets,
      collateral,
    })
  }
  return {
    whitelistFlags,
    balances,
  }
}
